/**
 * Phase 1E.3A — Album Inventory page.
 *
 * Reached via: My Collection → Open Album.
 * Shows the sticker inventory (auto-seeded on album creation) with filters,
 * search, per-row status/quantity edits, and bulk-status batching for
 * multi-select. Zero completion math — that arrives in a later phase.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Loader2, Package, ListChecks, Archive,
  Filter as FilterIcon, ChevronDown, Save, CheckSquare, Square, RotateCw,
  ClipboardPaste, Upload, X, CheckCircle2, AlertCircle, History,
  ExternalLink, Copy, Share2, Eye,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { track, EVENT } from "@/lib/analytics";
import { resolveCollectionTitle, resolveVariantSubtitle } from "@/lib/albumPresentation";

const STATUS_COLORS = {
  UNKNOWN:   "bg-neutral-100 text-neutral-800 border-neutral-300",
  OWNED:     "bg-emerald-100 text-emerald-900 border-emerald-300",
  MISSING:   "bg-red-100 text-red-900 border-red-300",
  DUPLICATE: "bg-blue-100 text-blue-900 border-blue-300",
};

export default function AlbumInventory() {
  const { album_id } = useParams();
  const nav = useNavigate();
  const [album, setAlbum] = useState(null);
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(""); // "" = All
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState("OWNED");
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [missingImportOpen, setMissingImportOpen] = useState(false);
  const [duplicateImportOpen, setDuplicateImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Phase A · Mark as Completed — confirmation dialog + submitting state.
  const [markCompleteOpen, setMarkCompleteOpen] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, s, sum, st] = await Promise.all([
        api.get(`/collector-auth/my-collection/albums/${album_id}`),
        api.get(`/collector-auth/my-collection/albums/${album_id}/inventory/stats`),
        api.get(`/collector-auth/my-collection/albums/${album_id}/summary`),
        api.get("/catalog/ownership-statuses"),
      ]);
      setAlbum(a.data);
      setStats(s.data);
      setSummary(sum.data);
      setStatuses(st.data.items || []);
      await loadInventory("", "");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load album.");
      if (e?.response?.status === 404) nav("/dashboard/my-collection", { replace: true });
    } finally { setLoading(false); }
  };

  const loadInventory = async (statusCode = statusFilter, searchQ = q) => {
    const params = {};
    if (statusCode) params.status_code = statusCode;
    if (searchQ.trim()) params.q = searchQ.trim();
    const r = await api.get(`/collector-auth/my-collection/albums/${album_id}/inventory`,
                             { params });
    setRows(r.data.items || []);
    setSelected(new Set());
  };

  const refreshStats = async () => {
    const [s, sum] = await Promise.all([
      api.get(`/collector-auth/my-collection/albums/${album_id}/inventory/stats`),
      api.get(`/collector-auth/my-collection/albums/${album_id}/summary`),
    ]);
    setStats(s.data);
    setSummary(sum.data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [album_id]);
  useEffect(() => { loadInventory(statusFilter, q); /* eslint-disable-next-line */ },
            [statusFilter]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => loadInventory(statusFilter, q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const updateRow = async (row, patch) => {
    try {
      const r = await api.patch(
        `/collector-auth/my-collection/albums/${album_id}/inventory/${row.official_sticker_id}`,
        patch);
      setRows(rs => rs.map(x => x.id === row.id
        ? { ...x, ...r.data }
        : x));
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed.");
    }
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const applyBulk = async () => {
    if (selected.size === 0) return;
    setApplyingBulk(true);
    try {
      const items = rows.filter(r => selected.has(r.id)).map(r => ({
        sticker_id: r.official_sticker_id,
        status_code: bulkTarget,
      }));
      await api.post(
        `/collector-auth/my-collection/albums/${album_id}/inventory/bulk-update`,
        { items });
      toast.success(`Updated ${items.length} sticker${items.length === 1 ? "" : "s"} → ${bulkTarget}.`);
      await loadInventory();
      await refreshStats();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d
        : Array.isArray(d?.errors) ? `${d.errors.length} row(s) failed validation.`
        : "Bulk update failed.");
    } finally { setApplyingBulk(false); }
  };

  if (loading || !album) {
    return (
      <div className="p-16 flex items-center justify-center" data-testid="ai-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  const cat = album.catalog || {};
  // Phase A · Album Title Hierarchy — canonical collection title as the
  // predominant page heading, variant descriptor as a smaller subtitle.
  // Eliminates the "Panini 2026 2026" duplication caused by
  // concatenating release.name + release.year on top of the variant.
  const collectionTitle = resolveCollectionTitle(album);
  const variantSubtitle = resolveVariantSubtitle(album, cat);
  const missingCount = Number(
    summary?.missing_summary?.total_missing_unique
    ?? summary?.by_status?.MISSING?.count
    ?? stats?.by_status?.MISSING?.count
    ?? 0
  );
  const isCompleted = album.status === "completed";
  // C4b.0.5 — checklist availability is a CATALOG signal, not an
  // inventory signal. Historical albums (2010/2014/2018/2022) have
  // no seeded canonical checklist yet → `checklist_size == 0` → hide
  // progress / stats / filters / inventory table + Import buttons.
  // Fresh onboarding 2026 albums have `checklist_size == 992` even
  // before any inventory row is created, so tracking controls stay
  // available and the first Import Missing click lazily hydrates the
  // 992 inventory rows via `hydrate_album_inventory`.
  //
  // Compat fallback (`summary.total_stickers > 0`) is retained for a
  // single release cycle so any surface that hasn't picked up the new
  // API field still lights up correctly. Remove once every consumer
  // has redeployed against the checklist_size contract.
  const hasChecklist = Number(album?.checklist_size || 0) > 0
                       || Number(summary?.total_stickers || 0) > 0;

  // C4b.0.5 — an album can be CONFIGURED (checklist_size > 0) but
  // UNHYDRATED (no album_sticker_inventory rows yet, so
  // summary.total_stickers === 0). This happens for every freshly-
  // onboarded 2026 album until the first Import Missing / Import
  // Duplicate commit hydrates the 992 rows. Show a neutral "ready to
  // track" projection instead of a misleading 0% / 0-of-0 header.
  const checklistSize = Number(album?.checklist_size || 0);
  const isUnhydrated = hasChecklist
                       && Number(summary?.total_stickers || 0) === 0
                       && checklistSize > 0;
  const projectedSummary = isUnhydrated
    ? {
        total_stickers: checklistSize,
        owned: 0, missing: 0, duplicate: 0,
        unknown: checklistSize, remaining: checklistSize,
        completion_percentage: 0,
        is_complete: false, is_near_completion: false,
        categories: [],
      }
    : summary;

  return (
    <div className="space-y-6" data-testid="album-inventory">
      <SEO title={`${collectionTitle} · Inventory · CollectorUp`} />

      <div>
        <div className="flex items-center gap-4 flex-wrap"
             data-testid="ai-nav-links">
          <Link to="/dashboard"
                className="text-xs uppercase tracking-widest text-[var(--smoke)] hover:text-[var(--ink)] flex items-center gap-1"
                data-testid="ai-back-to-profile">
            <ArrowLeft size={12} /> Back to My Profile
          </Link>
          <Link to="/dashboard/my-collection"
                className="text-xs uppercase tracking-widest text-[var(--smoke)] hover:text-[var(--ink)] flex items-center gap-1"
                data-testid="ai-see-my-collection">
            See My Collection <ArrowLeft size={12} className="rotate-180" />
          </Link>
        </div>
        <div className="flex flex-col gap-3 mt-2">
          <div className="min-w-0">
            {/* Phase A · Predominant page title = canonical collection */}
            <h1 className="font-display text-3xl md:text-4xl font-bold break-words"
                data-testid="ai-title">
              {collectionTitle}
            </h1>
            {/* Phase A · Secondary line = "USA · Hard Cover" */}
            {variantSubtitle && (
              <div className="text-sm md:text-base text-[var(--smoke)] mt-1"
                   data-testid="ai-subtitle">
                {variantSubtitle}
              </div>
            )}
            {album.nickname && (
              <div className="text-sm italic text-[var(--smoke)] mt-1"
                   data-testid="ai-nickname">
                &ldquo;{album.nickname}&rdquo;
              </div>
            )}
            <OwnerSharingControls album={album}
                                   onVisibilityChange={loadAll} />
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* C4a.6 — Sticker-tracking features (missing/duplicate import,
                history) require a seeded canonical checklist. WC 2026 has
                one (992 official stickers); the 2010/2014/2018/2022
                historical variants do not yet. We derive `hasChecklist`
                from the LIVE summary's `total_stickers` (the actual number
                of inventory rows generated for this album). Historical
                albums return 0 because `_stickers_for_variant` finds no
                catalog stickers for the release. If a future checklist
                ships for a historical release the gate lifts automatically
                — nothing else to change. Note: `album.total_required` and
                `album.sticker_count` are NOT surfaced by the album API
                and cannot be relied on here. */}
            {(() => {
              if (!hasChecklist) {
                return (
                  <div
                    className="text-[11px] uppercase tracking-widest font-bold text-[var(--smoke)] flex items-center gap-1"
                    data-testid="ai-checklist-coming-soon"
                  >
                    <ClipboardPaste size={12} /> Sticker tracking coming soon
                  </div>
                );
              }
              return (
                <div className="flex flex-wrap gap-2 justify-end" data-testid="ai-import-buttons">
                  <button
                    className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-4 py-2 text-sm font-bold uppercase tracking-widest hover:brightness-110 transition"
                    onClick={() => setMissingImportOpen(true)}
                    data-testid="ai-open-missing-import">
                    <ClipboardPaste size={14} /> Import Missing List
                  </button>
                  <button
                    className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-4 py-2 text-sm font-bold uppercase tracking-widest hover:brightness-110 transition"
                    onClick={() => setDuplicateImportOpen(true)}
                    data-testid="ai-open-duplicate-import">
                    <ClipboardPaste size={14} /> Import Duplicate List
                  </button>
                  <button
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-2 text-xs font-bold uppercase tracking-widest bg-white hover:bg-neutral-50 flex items-center gap-1"
                    onClick={() => setHistoryOpen(true)}
                    data-testid="ai-open-history">
                    <History size={12} /> History
                  </button>
                </div>
              );
            })()}
            {/* Phase A · Mark as Completed — success-styled call-to-
                action, shown only while the album is in progress.
                C4a — remains available regardless of checklist presence
                so historical albums can still be marked completed. */}
            {!isCompleted && (
              <button
                className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-800 bg-emerald-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-widest hover:bg-emerald-700 transition"
                onClick={() => setMarkCompleteOpen(true)}
                data-testid="ai-mark-complete-open">
                <CheckCircle2 size={14} /> Mark as Completed
              </button>
            )}
            {isCompleted && (
              <span
                className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-800 bg-emerald-50 text-emerald-900 px-4 py-2 text-sm font-bold uppercase tracking-widest"
                data-testid="ai-completed-badge">
                <CheckCircle2 size={14} /> Completed
              </span>
            )}
            <p className="text-xs text-[var(--smoke)] max-w-md text-right leading-relaxed"
               data-testid="ai-import-helper">
              Update your latest missing list and duplicate list. CollectorUp will automatically
              calculate which stickers you already own. You do not need to manually mark every
              sticker you own.
            </p>
          </div>
        </div>
      </div>

      {/* Completion header — live-computed, never stored.
          C4a — hidden for historical albums with no checklist so
          we never render "0% · 0 of 0" fake counts.
          C4b.0.5 — for unhydrated 2026 albums we swap the real (empty)
          summary for a projected one showing `checklist_size` as total
          and everything else as UNKNOWN, so the header renders honest
          "0% · 0 owned · 992 unknown" instead of a fake 0-of-0. */}
      {hasChecklist && projectedSummary && <CompletionHeader summary={projectedSummary} />}

      {/* Stats strip — same rule: hide for historical albums.
          C4b.0.5 — projected stats when unhydrated so the boxes show
          the real total and the correct unknown count. */}
      {hasChecklist && (isUnhydrated || stats) && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ai-stats">
          <StatBox testid="ai-stat-total"   label="Total stickers"
                    value={isUnhydrated ? checklistSize : stats.total_stickers}
                    Icon={Package} />
          {["UNKNOWN", "OWNED", "MISSING", "DUPLICATE"].map(code => {
            const s = isUnhydrated ? null : stats?.by_status?.[code];
            const fallback = isUnhydrated && code === "UNKNOWN" ? checklistSize : 0;
            return (
              <StatBox key={code} testid={`ai-stat-${code}`}
                        label={code.charAt(0) + code.slice(1).toLowerCase()}
                        value={s?.count ?? fallback}
                        sub={s?.quantity ? `qty ${s.quantity}` : ""}
                        tone={code} />
            );
          })}
        </div>
      )}

      {/* C4b.0.5 — Unhydrated helper banner. Configured checklist but
          zero inventory rows yet. Explains the neutral state and points
          the collector at the two flows that create inventory. */}
      {isUnhydrated && (
        <div className="border-2 border-dashed border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)] p-4 text-sm"
             data-testid="ai-unhydrated-hint">
          Your inventory is ready to track. Paste your latest missing
          or duplicate list to get started — CollectorUp will calculate
          which of the {checklistSize} stickers you already own.
        </div>
      )}

      {/* Filters / bulk / table are only meaningful when we have a
          seeded checklist. C4a — hide the whole tracker workbench for
          historical albums so no "no stickers match" empty-state text
          leaks through. */}
      {hasChecklist && (
        <>
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <FilterBtn label="All" active={statusFilter === ""} onClick={() => setStatusFilter("")}
                     testid="ai-filter-ALL" />
          {statuses.map(s => (
            <FilterBtn key={s.code} label={s.name}
                       active={statusFilter === s.code}
                       onClick={() => setStatusFilter(s.code)}
                       testid={`ai-filter-${s.code}`} tone={s.code} />
          ))}
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
              <input value={q} onChange={e => setQ(e.target.value)}
                     placeholder="Search sticker code or player…"
                     className="border-2 border-[var(--ink)] rounded-lg pl-8 pr-3 py-2 text-sm w-64"
                     data-testid="ai-search" />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)] p-3 flex flex-wrap items-center gap-3"
             data-testid="ai-bulk-bar">
          <div className="text-sm font-bold">
            {selected.size} sticker{selected.size === 1 ? "" : "s"} selected
          </div>
          <select value={bulkTarget} onChange={e => setBulkTarget(e.target.value)}
                  className="border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-sm bg-white"
                  data-testid="ai-bulk-target">
            {statuses.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
          <button onClick={applyBulk} disabled={applyingBulk}
                  className="btn-primary text-xs flex items-center gap-1"
                  data-testid="ai-bulk-apply">
            {applyingBulk ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Apply to selected
          </button>
          <button onClick={() => setSelected(new Set())}
                  className="text-xs underline"
                  data-testid="ai-bulk-clear">Clear selection</button>
        </div>
      )}

      {/* Table */}
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
        <div className="px-3 py-2 text-xs bg-neutral-100 border-b border-neutral-200 flex justify-between">
          <span data-testid="ai-count">
            Showing {rows.length} sticker{rows.length === 1 ? "" : "s"}
            {statusFilter && ` · filter: ${statusFilter}`}
            {q && ` · search: "${q}"`}
          </span>
          <button onClick={toggleSelectAll} className="text-xs underline"
                  data-testid="ai-select-all">
            {selected.size === rows.length && rows.length > 0 ? "Deselect all" : "Select all"}
          </button>
        </div>

        {rows.length === 0 ? (
          <div className="p-10 text-center text-[var(--smoke)]" data-testid="ai-empty">
            No stickers match the current filter.
            {statuses.length === 0 && (
              <div className="text-xs mt-2">
                (The variant may have no checklist stickers yet — add them from the admin catalog.)
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-table">
              <thead className="text-[10px] uppercase tracking-widest bg-neutral-50">
                <tr>
                  <th className="w-8 px-3 py-2"></th>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Qty</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const isSel = selected.has(row.id);
                  return (
                    <tr key={row.id} className="border-t border-neutral-200 hover:bg-neutral-50"
                        data-testid={`ai-row-${row.sticker?.sticker_number || row.official_sticker_id}`}>
                      <td className="px-3 py-2">
                        <button onClick={() => toggleSelected(row.id)}
                                aria-label={isSel ? "Deselect row" : "Select row"}
                                data-testid={`ai-select-${row.sticker?.sticker_number || row.id}`}>
                          {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono font-bold">
                        {row.sticker?.sticker_number || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div>{row.sticker?.name || "—"}</div>
                        {(row.sticker?.player_first_name || row.sticker?.player_last_name) && (
                          <div className="text-xs text-[var(--smoke)]">
                            {[row.sticker.player_first_name, row.sticker.player_last_name].filter(Boolean).join(" ")}
                            {row.sticker.player_position ? ` · ${row.sticker.player_position}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">
                        {row.category?.code || row.sticker?.category_id || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <StatusSelect
                          value={row.status_code}
                          statuses={statuses}
                          onChange={(code) => updateRow(row, { status_code: code })}
                          testid={`ai-status-${row.sticker?.sticker_number || row.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" min={0} max={999}
                          value={row.quantity}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(999, Number(e.target.value) || 0));
                            setRows(rs => rs.map(x => x.id === row.id ? { ...x, quantity: v } : x));
                          }}
                          onBlur={(e) => {
                            const v = Math.max(0, Math.min(999, Number(e.target.value) || 0));
                            if (v !== row.quantity) return;
                            updateRow(row, { quantity: v });
                          }}
                          className="border border-neutral-300 rounded px-2 py-1 w-16 text-right"
                          data-testid={`ai-qty-${row.sticker?.sticker_number || row.id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category breakdown — always visible, updates as inventory changes. */}
      {summary && summary.categories && summary.categories.length > 0 && (
        <CategoryBreakdown categories={summary.categories} />
      )}
      </>
      )}

      {missingImportOpen && (
        <MissingImportModal
          albumId={album_id}
          onClose={() => setMissingImportOpen(false)}
          onCommitted={async () => {
            setMissingImportOpen(false);
            await loadInventory();
            await refreshStats();
          }}
        />
      )}
      {duplicateImportOpen && (
        <DuplicateImportModal
          albumId={album_id}
          onClose={() => setDuplicateImportOpen(false)}
          onCommitted={async () => {
            setDuplicateImportOpen(false);
            await loadInventory();
            await refreshStats();
          }}
        />
      )}
      {historyOpen && (
        <ImportHistoryModal
          albumId={album_id}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* Phase A · Mark as Completed confirmation dialog. */}
      {markCompleteOpen && (
        <MarkCompleteModal
          missingCount={missingCount}
          submitting={markingComplete}
          onCancel={() => setMarkCompleteOpen(false)}
          onConfirm={async () => {
            setMarkingComplete(true);
            try {
              // Uses the existing album-status update pathway
              // (PATCH /albums/{id}). Backend clears the active
              // missing list, preserves duplicates + import history,
              // and stamps completed_at on the transition.
              await api.patch(
                `/collector-auth/my-collection/albums/${album_id}`,
                { status: "completed" }
              );
              toast.success("Album marked as completed.");
              track(EVENT.ALBUM_MARKED_COMPLETED || "album_marked_completed", {
                album_id,
              }, { once: true, key: `mark-complete-${album_id}` });
              setMarkCompleteOpen(false);
              await loadAll();
            } catch (e) {
              toast.error(
                formatApiError(e?.response?.data?.detail)
                || "Failed to mark album as completed."
              );
            } finally {
              setMarkingComplete(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Phase A · Mark as Completed confirmation dialog
// ═══════════════════════════════════════════════════════════════════
function MarkCompleteModal({ missingCount, submitting, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-mark-complete-title"
      data-testid="ai-mark-complete-modal"
    >
      <div className="w-full max-w-md rounded-2xl border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--ink)] overflow-hidden">
        <div className="px-5 pt-5">
          <h2
            id="ai-mark-complete-title"
            className="font-display text-2xl leading-tight"
            data-testid="ai-mark-complete-title"
          >
            Mark this album as completed?
          </h2>
          <p
            className="text-sm text-[var(--smoke)] mt-2 leading-relaxed"
            data-testid="ai-mark-complete-body"
          >
            This will mark your album as complete, set your progress to
            100%, and clear your active missing list. Your duplicate
            list and import history will be preserved.
          </p>
          {missingCount > 0 && (
            <div
              className="mt-3 rounded-lg border-2 border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900"
              data-testid="ai-mark-complete-warning"
            >
              You currently have <b data-testid="ai-mark-complete-missing-count">{missingCount}</b>{" "}
              {missingCount === 1 ? "sticker" : "stickers"} in your missing list.
              Marking the album as completed will clear the active missing list.
            </div>
          )}
        </div>
        <div className="px-5 py-4 mt-2 border-t border-neutral-200 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            className="rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-neutral-50"
            onClick={onCancel}
            disabled={submitting}
            data-testid="ai-mark-complete-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-800 bg-emerald-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-70"
            onClick={onConfirm}
            disabled={submitting}
            data-testid="ai-mark-complete-confirm"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {submitting ? "Marking…" : "Mark as Completed"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Building blocks
// ═══════════════════════════════════════════════════════════════════
function StatBox({ label, value, sub, tone, testid, Icon }) {
  const cls = tone && STATUS_COLORS[tone] ? "border-b-4 " + STATUS_COLORS[tone].split(" ").find(x => x.startsWith("border-")) : "";
  return (
    <div className={"border-2 border-[var(--ink)] rounded-2xl bg-white p-4 " + cls}
         data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--smoke)]">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className="text-3xl font-display mt-1" data-testid={`${testid}-value`}>
        {value ?? 0}
      </div>
      {sub && <div className="text-xs text-[var(--smoke)] mt-1">{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Completion header — live progress bar + counts + badges
// ═══════════════════════════════════════════════════════════════════
function CompletionHeader({ summary }) {
  const pct = summary.completion_percentage || 0;
  const bar = pct >= 100 ? "bg-emerald-500"
            : pct >= 95   ? "bg-emerald-400"
            : pct >= 50   ? "bg-[var(--panini-yellow)]"
            : pct > 0     ? "bg-orange-400"
            : "bg-neutral-300";
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[4px_4px_0_var(--ink)]"
             data-testid="ai-completion-header">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Completion</div>
          <div className="text-5xl font-display" data-testid="ai-completion-pct">
            {pct}<span className="text-2xl">%</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {summary.is_complete && (
            <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-emerald-600 text-white"
                  data-testid="ai-badge-complete">Completed</span>
          )}
          {!summary.is_complete && summary.is_near_completion && (
            <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border-2 border-emerald-500 text-emerald-900 bg-emerald-50"
                  data-testid="ai-badge-near">Near completion</span>
          )}
        </div>
      </div>
      <div className="h-3 mt-3 bg-neutral-200 rounded-full overflow-hidden"
           role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
           data-testid="ai-progress-bar">
        <div className={"h-full transition-[width] " + bar}
             style={{width: `${Math.min(100, Math.max(0, pct))}%`}} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4">
        <TinyStat label="Total"     value={summary.total_stickers} testid="ai-comp-total" />
        <TinyStat label="Owned"     value={summary.owned}       tone="OWNED"     testid="ai-comp-owned" />
        <TinyStat label="Duplicate" value={summary.duplicate}   tone="DUPLICATE" testid="ai-comp-duplicate" />
        <TinyStat label="Missing"   value={summary.missing}     tone="MISSING"   testid="ai-comp-missing" />
        <TinyStat label="Unknown"   value={summary.unknown}     tone="UNKNOWN"   testid="ai-comp-unknown" />
        <TinyStat label="Remaining" value={summary.remaining}   tone="UNKNOWN"   testid="ai-comp-remaining" />
      </div>
    </section>
  );
}

const TS_TONE = {
  OWNED:     "border-emerald-300 bg-emerald-50 text-emerald-900",
  DUPLICATE: "border-blue-300    bg-blue-50    text-blue-900",
  MISSING:   "border-red-300     bg-red-50     text-red-900",
  UNKNOWN:   "border-neutral-300 bg-neutral-50 text-neutral-800",
};
function TinyStat({ label, value, tone, testid }) {
  return (
    <div className={"border-2 rounded-lg text-center py-2 " + (TS_TONE[tone] || "border-neutral-300 bg-white")}
         data-testid={testid}>
      <div className="text-2xl font-display" data-testid={`${testid}-value`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest">{label}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Category breakdown — per-category completion
// ═══════════════════════════════════════════════════════════════════
function CategoryBreakdown({ categories }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-4"
             data-testid="ai-category-breakdown">
      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-3">
        Category breakdown
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {categories.map(c => {
          const pct = c.completion_percentage || 0;
          const bar = pct >= 100 ? "bg-emerald-500"
                    : pct >= 50   ? "bg-[var(--panini-yellow)]"
                    : pct > 0     ? "bg-orange-400"
                    : "bg-neutral-300";
          return (
            <div key={c.category_code} className="border border-neutral-200 rounded-lg p-3"
                 data-testid={`ai-cat-${c.category_code}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-bold text-sm">{c.category_name}</div>
                <div className="text-xs font-mono">{c.owned + c.duplicate}/{c.total}</div>
              </div>
              <div className="text-xs text-[var(--smoke)]">
                {pct}% · {c.remaining} remaining
              </div>
              <div className="h-1.5 mt-2 bg-neutral-200 rounded-full overflow-hidden">
                <div className={"h-full " + bar}
                     style={{width: `${Math.min(100, Math.max(0, pct))}%`}} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FilterBtn({ label, active, onClick, testid, tone }) {
  const activeCls = tone && STATUS_COLORS[tone]
    ? STATUS_COLORS[tone] + " border-2 font-bold"
    : "bg-[var(--ink)] text-white border-2 border-[var(--ink)]";
  return (
    <button onClick={onClick} data-testid={testid}
            className={"text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-full transition " +
              (active ? activeCls : "bg-white text-[var(--ink)] border-2 border-neutral-300 hover:border-[var(--ink)]")}>
      {label}
    </button>
  );
}

function StatusSelect({ value, statuses, onChange, testid }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
            data-testid={testid}
            className={"text-xs font-bold border-2 rounded-full px-2 py-1 " +
              (STATUS_COLORS[value] || STATUS_COLORS.UNKNOWN)}>
      {statuses.map(s => (
        <option key={s.code} value={s.code}>{s.name}</option>
      ))}
    </select>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Missing Sticker Import — modal (Paste → Preview → Confirm → Summary)
// ═══════════════════════════════════════════════════════════════════
function MissingImportModal({ albumId, onClose, onCommitted }) {
  const [step, setStep] = useState("paste"); // paste | preview | committing | summary
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const doPreview = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(
        `/collector-auth/my-collection/albums/${albumId}/imports/missing/preview`,
        { text });
      setPreview(r.data);
      setStep("preview");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Preview failed.");
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true); setErr(""); setStep("committing");
    try {
      const r = await api.post(
        `/collector-auth/my-collection/albums/${albumId}/imports/missing/commit`,
        { text, notes: notes.trim() || null });
      setResult(r.data.result);
      setStep("summary");
      // Spec §5: no album_id transmitted. `import_item_count` covers
      // both imported + skipped.
      track(EVENT.MISSING_LIST_IMPORTED, {
        album_year:        r.data?.result?.album_year || undefined,
        import_item_count: (r.data.result?.imported || 0) + (r.data.result?.skipped || 0),
      }, { once: true, key: `missing-import-${albumId}-${Date.now()}` });
      toast.success(`Marked ${r.data.result.imported} sticker${r.data.result.imported === 1 ? "" : "s"} as missing.`);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Commit failed.");
      setStep("preview");
    } finally { setBusy(false); }
  };

  const summary = preview?.summary;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
         role="dialog" aria-modal="true"
         data-testid="missing-import-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white border-2 border-[var(--ink)] shadow-2xl w-full max-w-3xl rounded-t-3xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 flex items-start justify-between gap-3 border-b bg-[var(--panini-yellow-soft)]">
          <div>
            <div className="font-display text-xl">Import Missing List</div>
            <div className="text-xs text-[var(--smoke)] mt-1">
              Paste → Preview → Confirm → Summary
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/60" aria-label="Close"
                  data-testid="mi-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === "paste" && (
            <div className="space-y-3" data-testid="mi-paste-step">
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Paste your missing list
                </span>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="ARG17 ARG18 BRA5 FWC03 …"
                  rows={10}
                  className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 font-mono text-sm"
                  data-testid="mi-textarea"
                />
              </label>
              <MiField label="Notes (optional)" value={notes} onChange={setNotes}
                     placeholder="Where did this list come from?" testid="mi-notes" />
              <div className="text-xs text-[var(--smoke)]">
                Nothing is written until you confirm on the preview screen.
              </div>
              {err && <BannerErr>{err}</BannerErr>}
            </div>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4" data-testid="mi-preview-step">
              {(() => {
                const uniqueMissing = summary.unique_missing ?? summary.valid_to_import ?? 0;
                const checklistSize = preview.checklist_size ?? 992;
                const owned = Math.max(0, checklistSize - uniqueMissing);
                const progress = checklistSize > 0
                  ? Math.round((owned / checklistSize) * 1000) / 10
                  : 0;
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="mi-summary-metrics">
                    <SmallStat label="Valid" value={uniqueMissing} tone="OWNED" testid="mi-sum-valid" />
                    <SmallStat label="Unknown" value={summary.unknown} tone="MISSING" testid="mi-sum-unknown" />
                    <SmallStat label="Unique missing" value={uniqueMissing} tone="MISSING" testid="mi-sum-unique-missing" />
                    <SmallStat label="Owned" value={owned} tone="OWNED" testid="mi-sum-owned" />
                    <SmallStat label="Progress" value={`${progress}%`} tone="DUPLICATE" testid="mi-sum-progress" />
                    <SmallStat label="Invalid" value={summary.invalid} tone="MISSING" testid="mi-sum-invalid" />
                    <SmallStat label="Already missing" value={summary.already_missing} testid="mi-sum-already" />
                    <SmallStat label="Total tokens" value={summary.total_tokens} testid="mi-sum-total" />
                  </div>
                );
              })()}

              <details className="border rounded-lg" open>
                <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest font-bold">
                  Row-by-row preview
                </summary>
                <div className="max-h-72 overflow-y-auto">
                  {/* Mobile: stacked cards (<md). Desktop: table (md+). */}
                  <ul className="md:hidden divide-y" data-testid="mi-preview-cards">
                    {preview.entries.map((e, i) => (
                      <li key={`mi-c-${e.row}-${i}`}
                          className="px-3 py-2 text-xs"
                          data-testid={`mi-preview-row-${e.raw}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-bold break-all">{e.raw}</span>
                              <span className="text-[10px] text-[var(--smoke)] uppercase tracking-widest">
                                Row {e.row}
                              </span>
                            </div>
                            <div className="mt-1 break-words">{e.name || "—"}</div>
                            {e.reason && (
                              <div className="mt-1 text-[var(--smoke)] break-words">
                                {e.reason}
                              </div>
                            )}
                          </div>
                          <OutcomePill outcome={e.outcome} />
                        </div>
                      </li>
                    ))}
                  </ul>
                  <table className="hidden md:table w-full text-xs" data-testid="mi-preview-table">
                    <thead>
                      <tr className="bg-neutral-100 text-[10px] uppercase tracking-widest">
                        <th className="text-left px-3 py-1">Row</th>
                        <th className="text-left px-3 py-1">Code</th>
                        <th className="text-left px-3 py-1">Sticker</th>
                        <th className="text-left px-3 py-1">Outcome</th>
                        <th className="text-left px-3 py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.entries.map((e, i) => (
                        <tr key={`mi-e-${e.row}-${i}`} className="border-t"
                            data-testid={`mi-preview-row-desktop-${e.raw}`}>
                          <td className="px-3 py-1">{e.row}</td>
                          <td className="px-3 py-1 font-mono">{e.raw}</td>
                          <td className="px-3 py-1">{e.name || "—"}</td>
                          <td className="px-3 py-1"><OutcomePill outcome={e.outcome} /></td>
                          <td className="px-3 py-1 text-[var(--smoke)]">{e.reason || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>

              {err && <BannerErr>{err}</BannerErr>}

              {summary.valid_to_import === 0 && (
                <div className="text-sm bg-yellow-50 border border-yellow-300 text-yellow-900 rounded p-3"
                     data-testid="mi-nothing-to-import">
                  No changes will be made — every valid code is already marked MISSING.
                </div>
              )}
            </div>
          )}

          {step === "committing" && (
            <div className="p-12 text-center text-sm text-[var(--smoke)]" data-testid="mi-committing">
              <Loader2 className="animate-spin inline mr-2" /> Applying import…
            </div>
          )}

          {step === "summary" && result && (
            <div className="space-y-4 text-center" data-testid="mi-summary-step">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 grid place-items-center">
                <CheckCircle2 size={28} className="text-emerald-700" />
              </div>
              <div className="font-display text-2xl">Import complete</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-xl mx-auto">
                <SmallStat label="Imported" value={result.imported} tone="OWNED" testid="mi-result-imported" />
                <SmallStat label="Owned → missing" value={result.currently_owned_flipped} tone="DUPLICATE" testid="mi-result-flipped" />
                <SmallStat label="Already missing" value={result.skipped_already_missing} tone="MISSING" testid="mi-result-skipped" />
                <SmallStat label="Duplicates in file" value={result.duplicates} testid="mi-result-dupes" />
                <SmallStat label="Unknown" value={result.unknown} testid="mi-result-unknown" />
                <SmallStat label="Invalid" value={result.invalid} testid="mi-result-invalid" />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-neutral-50 flex flex-wrap items-center justify-between gap-2">
          {step === "paste" && (
            <>
              <div className="text-xs text-[var(--smoke)]">Formatting is normalized automatically.</div>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn-primary flex items-center gap-1"
                        disabled={busy || !text.trim()}
                        onClick={doPreview}
                        data-testid="mi-preview-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <ListChecks size={14} />}
                  Preview
                </button>
              </div>
            </>
          )}
          {step === "preview" && summary && (
            <>
              <button className="text-xs underline" onClick={() => setStep("paste")}
                      data-testid="mi-back">← Edit paste</button>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn-primary flex items-center gap-1"
                        disabled={busy || (summary.unique_missing ?? summary.valid_to_import ?? 0) === 0}
                        onClick={doCommit}
                        data-testid="mi-commit-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Confirm &amp; mark {summary.unique_missing ?? summary.valid_to_import ?? 0} as missing
                </button>
              </div>
            </>
          )}
          {step === "summary" && (
            <div className="w-full flex justify-end">
              <button className="btn-primary" onClick={onCommitted}
                      data-testid="mi-done">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportHistoryModal({ albumId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [m, d] = await Promise.all([
          api.get(`/collector-auth/my-collection/albums/${albumId}/imports/missing/history`),
          api.get(`/collector-auth/my-collection/albums/${albumId}/imports/duplicate/history`),
        ]);
        const merged = [
          ...(m.data.items || []).map(x => ({...x, _kind: "missing"})),
          ...(d.data.items || []).map(x => ({...x, _kind: "duplicate"})),
        ];
        merged.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setRows(merged);
      } finally { setLoading(false); }
    })();
  }, [albumId]);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
         role="dialog" aria-modal="true"
         data-testid="import-history-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white border-2 border-[var(--ink)] shadow-2xl w-full max-w-3xl rounded-t-3xl sm:rounded-2xl overflow-hidden max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 flex items-start justify-between gap-3 border-b bg-neutral-100">
          <div>
            <div className="font-display text-xl">Import history</div>
            <div className="text-xs text-[var(--smoke)] mt-1">
              Last {rows.length} import batch{rows.length === 1 ? "" : "es"} for this album.
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/60" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="animate-spin inline" /></div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-[var(--smoke)]" data-testid="ih-empty">
              No imports yet.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="ih-table">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-left px-3 py-2">Actor</th>
                  <th className="text-right px-3 py-2">Rows</th>
                  <th className="text-right px-3 py-2">Imported</th>
                  <th className="text-right px-3 py-2">Extra</th>
                  <th className="text-right px-3 py-2">Invalid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={`${r._kind}-${r.id}`} className="border-t" data-testid={`ih-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <span className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest " +
                        (r._kind === "duplicate" ? "bg-blue-100 text-blue-900" : "bg-red-100 text-red-900")}>
                        {r._kind}
                      </span>
                    </td>
                    <td className="px-3 py-2">{r.actor_email}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.summary?.total_tokens}</td>
                    <td className="px-3 py-2 text-right font-mono">{(r.updated_sticker_ids || []).length}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r._kind === "duplicate"
                        ? (r.summary?.total_quantity_delta ?? 0)
                        : (r.summary?.already_missing ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {(r.summary?.invalid ?? r.summary?.invalid_rows ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function SmallStat({ label, value, sub, tone, testid }) {
  const cls = tone && STATUS_COLORS[tone] ? STATUS_COLORS[tone] : "bg-neutral-100";
  return (
    <div className={"border rounded-lg p-2 text-center " + cls} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest">{label}</div>
      <div className="text-xl font-display" data-testid={`${testid}-value`}>{value ?? 0}</div>
      {sub && <div className="text-[10px] text-[var(--smoke)]">{sub}</div>}
    </div>
  );
}
function OutcomePill({ outcome }) {
  const m = {
    valid_new:         ["bg-emerald-100 text-emerald-900", "Will import"],
    valid_change:      ["bg-yellow-100 text-yellow-900",   "Owned → missing"],
    already_missing:   ["bg-neutral-200 text-neutral-800", "Already missing"],
    duplicate_in_file: ["bg-blue-100 text-blue-900",       "Duplicate"],
    unknown:           ["bg-red-100 text-red-900",         "Unknown"],
    invalid:           ["bg-red-100 text-red-900",         "Invalid"],
  }[outcome] || ["bg-neutral-100 text-neutral-700", outcome];
  return <span className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest " + m[0]}>{m[1]}</span>;
}
function BannerErr({ children }) {
  return (
    <div className="border border-red-300 bg-red-50 text-red-900 rounded p-3 text-sm flex items-start gap-2">
      <AlertCircle size={14} className="mt-0.5" /> <span>{children}</span>
    </div>
  );
}
function MiField({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">{label}</span>
      <input value={value ?? ""} onChange={e => onChange(e.target.value)}
             placeholder={placeholder}
             className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white"
             data-testid={testid} />
    </label>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Duplicate Sticker Import — modal (mirrors Missing Import; tracks qty)
// ═══════════════════════════════════════════════════════════════════
function DuplicateImportModal({ albumId, onClose, onCommitted }) {
  const [step, setStep] = useState("paste");
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const doPreview = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(
        `/collector-auth/my-collection/albums/${albumId}/imports/duplicate/preview`,
        { text });
      setPreview(r.data);
      setStep("preview");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Preview failed.");
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true); setErr(""); setStep("committing");
    try {
      const r = await api.post(
        `/collector-auth/my-collection/albums/${albumId}/imports/duplicate/commit`,
        { text, notes: notes.trim() || null });
      setResult(r.data.result);
      setStep("summary");
      track(EVENT.DUPLICATE_LIST_IMPORTED, {
        album_year:        r.data?.result?.album_year || undefined,
        import_item_count: (r.data.result?.imported || 0) + (r.data.result?.updated || 0),
      }, { once: true, key: `dup-import-${albumId}-${Date.now()}` });
      toast.success(
        `Duplicate import applied: ${r.data.result.imported} new, `
        + `${r.data.result.updated} increased, +${r.data.result.total_quantity_delta} qty.`);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Commit failed.");
      setStep("preview");
    } finally { setBusy(false); }
  };

  const summary = preview?.summary;
  const changes = preview?.changes || [];

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
         role="dialog" aria-modal="true"
         data-testid="duplicate-import-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white border-2 border-[var(--ink)] shadow-2xl w-full max-w-3xl rounded-t-3xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 flex items-start justify-between gap-3 border-b bg-blue-50">
          <div>
            <div className="font-display text-xl">Import Duplicate List</div>
            <div className="text-xs text-[var(--smoke)] mt-1">
              Paste → Preview → Confirm → Summary · repeated codes accumulate quantity
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/60" aria-label="Close"
                  data-testid="di-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === "paste" && (
            <div className="space-y-3" data-testid="di-paste-step">
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Paste your duplicate list
                </span>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="ARG17&#10;ARG17&#10;ARG17&#10;BRA5, FWC03…&#10;Repeats accumulate — three ARG17s → quantity = 3."
                  rows={10}
                  className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 font-mono text-sm"
                  data-testid="di-textarea"
                />
              </label>
              <MiField label="Notes (optional)" value={notes} onChange={setNotes}
                       placeholder="Where did this list come from?" testid="di-notes" />
              <div className="text-xs text-[var(--smoke)] space-y-1"
                   data-testid="di-quantity-helper">
                <div>
                  <strong className="text-[var(--ink)]">Quantities represent extra copies available for trade</strong> —
                  the single copy already in your album is separate and is never included in this number.
                </div>
                <div>
                  Existing DUPLICATE quantities are increased — never overwritten. Cap: 999 per sticker.
                </div>
              </div>
              {err && <BannerErr>{err}</BannerErr>}
            </div>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4" data-testid="di-preview-step">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SmallStat label="Total tokens" value={summary.total_tokens} testid="di-sum-total" />
                <SmallStat label="Unique codes" value={summary.unique_codes} testid="di-sum-unique" />
                <SmallStat label="New duplicates" value={summary.valid_new_codes} tone="DUPLICATE" testid="di-sum-new" />
                <SmallStat label="Qty increases" value={summary.increase_quantity_codes} tone="OWNED" testid="di-sum-increase" />
                <SmallStat label="Qty delta" value={summary.total_quantity_delta} tone="DUPLICATE" testid="di-sum-delta" />
                <SmallStat label="Capped at 999" value={summary.codes_capped_at_max} testid="di-sum-capped" />
                <SmallStat label="Unknown" value={summary.unknown_rows} tone="MISSING" testid="di-sum-unknown" />
                <SmallStat label="Invalid" value={summary.invalid_rows} tone="MISSING" testid="di-sum-invalid" />
              </div>

              {changes.length > 0 && (
                <details className="border rounded-lg" open>
                  <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest font-bold">
                    Quantity changes ({changes.length} sticker{changes.length === 1 ? "" : "s"})
                  </summary>
                  <div className="max-h-60 overflow-y-auto">
                    {/* Mobile: stacked cards. Desktop: table. */}
                    <ul className="md:hidden divide-y" data-testid="di-changes-cards">
                      {changes.map(c => (
                        <li key={c.sticker_id}
                            className="px-3 py-2 text-xs"
                            data-testid={`di-change-${c.sticker_number}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono font-bold break-all">{c.sticker_number}</span>
                                <span className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest " +
                                  (c.action === "increase_quantity"
                                    ? "bg-yellow-100 text-yellow-900"
                                    : "bg-blue-100 text-blue-900")}>
                                  {c.action === "increase_quantity" ? "increase" : "new"}
                                </span>
                              </div>
                              <div className="mt-1 break-words">{c.name}</div>
                            </div>
                          </div>
                          <dl className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                            <div>
                              <dt className="text-[9px] uppercase tracking-widest text-[var(--smoke)]">In paste</dt>
                              <dd className="font-mono">{c.count_in_paste}</dd>
                            </div>
                            <div>
                              <dt className="text-[9px] uppercase tracking-widest text-[var(--smoke)]">Current</dt>
                              <dd className="font-mono">
                                {c.current_status === "DUPLICATE" ? c.current_quantity : "—"}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-[9px] uppercase tracking-widest text-[var(--smoke)]">Final</dt>
                              <dd className={"font-mono " + (c.would_be_capped ? "text-orange-700 font-bold" : "")}>
                                {c.final_quantity}{c.would_be_capped ? " ★" : ""}
                              </dd>
                            </div>
                          </dl>
                        </li>
                      ))}
                    </ul>
                    <table className="hidden md:table w-full text-xs" data-testid="di-changes-table">
                      <thead>
                        <tr className="bg-neutral-100 text-[10px] uppercase tracking-widest">
                          <th className="text-left px-3 py-1">Code</th>
                          <th className="text-left px-3 py-1">Name</th>
                          <th className="text-right px-3 py-1">In paste</th>
                          <th className="text-right px-3 py-1">Current</th>
                          <th className="text-right px-3 py-1">Final</th>
                          <th className="text-left px-3 py-1">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changes.map(c => (
                          <tr key={c.sticker_id} className="border-t"
                              data-testid={`di-change-desktop-${c.sticker_number}`}>
                            <td className="px-3 py-1 font-mono font-bold">{c.sticker_number}</td>
                            <td className="px-3 py-1">{c.name}</td>
                            <td className="px-3 py-1 text-right font-mono">{c.count_in_paste}</td>
                            <td className="px-3 py-1 text-right font-mono">
                              {c.current_status === "DUPLICATE" ? c.current_quantity : "—"}
                            </td>
                            <td className={"px-3 py-1 text-right font-mono " +
                              (c.would_be_capped ? "text-orange-700 font-bold" : "")}>
                              {c.final_quantity}{c.would_be_capped ? " ★" : ""}
                            </td>
                            <td className="px-3 py-1">
                              <span className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest " +
                                (c.action === "increase_quantity"
                                  ? "bg-yellow-100 text-yellow-900"
                                  : "bg-blue-100 text-blue-900")}>
                                {c.action === "increase_quantity" ? "increase" : "new"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              <details className="border rounded-lg">
                <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest font-bold">
                  Row-by-row parse ({preview.entries.length})
                </summary>
                <div className="max-h-56 overflow-y-auto">
                  {/* Mobile: stacked cards. Desktop: table. */}
                  <ul className="md:hidden divide-y" data-testid="di-preview-cards">
                    {preview.entries.map((e, i) => (
                      <li key={`di-c-${e.row}-${i}`}
                          className="px-3 py-2 text-xs"
                          data-testid={`di-preview-row-${i + 1}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-bold break-all">{e.raw}</span>
                              <span className="text-[10px] text-[var(--smoke)] uppercase tracking-widest">
                                Row {e.row}
                              </span>
                            </div>
                            {e.reason && (
                              <div className="mt-1 text-[var(--smoke)] break-words">
                                {e.reason}
                              </div>
                            )}
                          </div>
                          <DupOutcomePill outcome={e.outcome} />
                        </div>
                      </li>
                    ))}
                  </ul>
                  <table className="hidden md:table w-full text-xs" data-testid="di-preview-table">
                    <thead>
                      <tr className="bg-neutral-100 text-[10px] uppercase tracking-widest">
                        <th className="text-left px-3 py-1">Row</th>
                        <th className="text-left px-3 py-1">Code</th>
                        <th className="text-left px-3 py-1">Outcome</th>
                        <th className="text-left px-3 py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.entries.map((e, i) => (
                        <tr key={`di-e-${e.row}-${i}`} className="border-t"
                            data-testid={`di-preview-row-desktop-${i + 1}`}>
                          <td className="px-3 py-1">{e.row}</td>
                          <td className="px-3 py-1 font-mono">{e.raw}</td>
                          <td className="px-3 py-1"><DupOutcomePill outcome={e.outcome} /></td>
                          <td className="px-3 py-1 text-[var(--smoke)]">{e.reason || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>

              {err && <BannerErr>{err}</BannerErr>}

              {summary.valid_new_codes + summary.increase_quantity_codes === 0 && (
                <div className="text-sm bg-yellow-50 border border-yellow-300 text-yellow-900 rounded p-3"
                     data-testid="di-nothing-to-import">
                  No changes will be made — the paste contains no valid codes for this album.
                </div>
              )}
            </div>
          )}

          {step === "committing" && (
            <div className="p-12 text-center text-sm text-[var(--smoke)]" data-testid="di-committing">
              <Loader2 className="animate-spin inline mr-2" /> Applying duplicate import…
            </div>
          )}

          {step === "summary" && result && (
            <div className="space-y-4 text-center" data-testid="di-summary-step">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 grid place-items-center">
                <CheckCircle2 size={28} className="text-blue-700" />
              </div>
              <div className="font-display text-2xl">Duplicate import complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-w-2xl mx-auto">
                <SmallStat label="New DUPLICATE"    value={result.imported}          tone="DUPLICATE" testid="di-result-imported" />
                <SmallStat label="Qty increases"    value={result.updated}            tone="OWNED"     testid="di-result-updated" />
                <SmallStat label="Total qty delta"  value={result.total_quantity_delta} tone="DUPLICATE" testid="di-result-delta" />
                <SmallStat label="Duplicate rows"   value={result.duplicate_entries_counted} testid="di-result-dupes" />
                <SmallStat label="Unknown"          value={result.unknown}                testid="di-result-unknown" />
                <SmallStat label="Invalid"          value={result.invalid}                testid="di-result-invalid" />
                <SmallStat label="Capped at 999"    value={result.codes_capped_at_max}    testid="di-result-capped" />
                <SmallStat label="Total tokens"     value={result.total_tokens}           testid="di-result-total" />
              </div>
              {result.final_quantities?.length > 0 && (
                <details className="text-left border rounded-lg mt-4">
                  <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest font-bold">
                    Final quantities
                  </summary>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
                    {result.final_quantities.map(f => (
                      <div key={f.sticker_number} className="flex justify-between border-b py-0.5">
                        <span>{f.sticker_number}</span>
                        <span className="font-bold">{f.quantity}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-neutral-50 flex flex-wrap items-center justify-between gap-2">
          {step === "paste" && (
            <>
              <div className="text-xs text-[var(--smoke)]">Repeats are counted, not flagged.</div>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn-primary flex items-center gap-1"
                        disabled={busy || !text.trim()}
                        onClick={doPreview}
                        data-testid="di-preview-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <ListChecks size={14} />}
                  Preview
                </button>
              </div>
            </>
          )}
          {step === "preview" && summary && (
            <>
              <button className="text-xs underline" onClick={() => setStep("paste")}
                      data-testid="di-back">← Edit paste</button>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn-primary flex items-center gap-1"
                        disabled={busy
                          || (summary.valid_new_codes + summary.increase_quantity_codes) === 0}
                        onClick={doCommit}
                        data-testid="di-commit-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Confirm · {summary.valid_new_codes} new + {summary.increase_quantity_codes} increases
                </button>
              </div>
            </>
          )}
          {step === "summary" && (
            <div className="w-full flex justify-end">
              <button className="btn-primary" onClick={onCommitted}
                      data-testid="di-done">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DupOutcomePill({ outcome }) {
  const m = {
    valid_new:         ["bg-blue-100 text-blue-900",       "New duplicate"],
    increase_quantity: ["bg-yellow-100 text-yellow-900",   "Increase qty"],
    additional_count:  ["bg-neutral-200 text-neutral-800", "+1 qty"],
    unknown:           ["bg-red-100 text-red-900",         "Unknown"],
    invalid:           ["bg-red-100 text-red-900",         "Invalid"],
  }[outcome] || ["bg-neutral-100 text-neutral-700", outcome];
  return <span className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest " + m[0]}>{m[1]}</span>;
}


// ═══════════════════════════════════════════════════════════════════
// Owner sharing controls (visible on the authenticated Album page).
// ═══════════════════════════════════════════════════════════════════
function OwnerSharingControls({ album, onVisibilityChange }) {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let ignore = false;
    api.get("/collector-auth/me").then(r => {
      if (!ignore) setMe(r.data?.collector || null);
    }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  if (!me) return null;
  const publicUrl = album.public_slug && me.username
    ? `${window.location.origin}/c/${me.username}/albums/${album.public_slug}`
    : null;
  const isPublic = album.visibility === "public";

  const makePublic = async () => {
    setBusy(true);
    try {
      await api.patch(`/collector-auth/my-collection/albums/${album.id}`,
                       { visibility: "public" });
      toast.success("Album is now public. Share your link!");
      onVisibilityChange && onVisibilityChange();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to make public.");
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Public link copied.");
  };

  const share = async () => {
    if (!publicUrl) return;
    if (navigator.share) {
      try { await navigator.share({ title: album.nickname || "Album", url: publicUrl }); } catch {}
    } else {
      await copyLink();
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ai-owner-controls">
      {isPublic ? (
        <>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer"
             className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest bg-white hover:bg-neutral-50 flex items-center gap-1"
             data-testid="ai-view-public">
            <Eye size={12} /> View Public Page <ExternalLink size={10} />
          </a>
          <button onClick={copyLink}
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest bg-white hover:bg-neutral-50 flex items-center gap-1"
                  data-testid="ai-copy-public-link">
            <Copy size={12} /> Copy Public Link
          </button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button onClick={share}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest bg-white hover:bg-neutral-50 flex items-center gap-1"
                    data-testid="ai-share">
              <Share2 size={12} /> Share
            </button>
          )}
          <span className="text-[10px] font-mono text-emerald-700 uppercase tracking-widest">
            · public
          </span>
        </>
      ) : (
        <button onClick={makePublic} disabled={busy}
                className="btn-primary text-xs flex items-center gap-1"
                data-testid="ai-make-public">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
          Make Public to Share
        </button>
      )}
    </div>
  );
}

